import { ArrowLeftIcon } from '@/components/Icons';
import { HorizontalContainer, VerticalContainer } from '@/components';
import { useEffect } from 'react';

type BmiResultProps = {
    setPageIndex: (arg: number) => void;
    bmi: number;
    height: number;
    weight: number;
};

export function BmiResult({
    setPageIndex,
    bmi,
    height,
    weight,
}: BmiResultProps) {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    function goBack() {
        setPageIndex(0);
    }

    function getPointerPosition() {
        if (bmi <= 10) {
            return '0%';
        } else if (bmi >= 40) {
            return '100%';
        } else {
            const pointerWidth = (bmi - 10) * (100 / 30);
            return pointerWidth + '%';
        }
    }

    function getText() {
        if (bmi < 18.5) {
            return 'Dit BMI indikerer at du er undervægtig, og du er derfor ikke egnet til medicinsk vægttab.';
        } else if (bmi < 25) {
            return 'Dit BMI indikerer at du er normalvægtig, og du er derfor ikke egnet til medicinsk vægttab.';
        } else if (bmi < 27) {
            return (
                <div>
                    Dit BMI indikerer lettere overvægt, hvilket ikke gør dig
                    egnet til medicinsk vægttab. For en yderligere forklaring
                    kan du skrive til{' '}
                    <a className="orange" href="mailto:kontakt@hejdoktor.dk">
                        kontakt@hejdoktor.dk
                    </a>
                    .
                </div>
            );
        } else if (bmi < 30) {
            return (
                <div>
                    Dit BMI indikerer overvægt, og du er måske egnet til
                    medicinsk vægttab. Du har mulighed for at følge Hejdoktors
                    vægttabsforløb, hvis du eksempelvis har en eller flere af
                    følgende vægtrelaterede lidelser og risikofaktorer:
                    <br />
                    <br />
                    <ul>
                        <li>&bull; Forhøjet blodtryk</li>
                        <li>&bull; Forhøjet kolesterol</li>
                        <li>&bull; Dyslipidæmi</li>
                        <li>&bull; Kardiovaskulær sygdom</li>
                        <li>&bull; Obstruktiv søvnapnø</li>
                        <li>&bull; Vægtrelaterede smertetilstande i led</li>
                        <li>&bull; Dysglykæmi (prædiabetes)</li>
                    </ul>
                    <br />
                    Når du har tilmeldt dig forløbet, vil vores læger vurdere
                    din situation. Hvis det viser sig, at du ikke er egnet, vil
                    du få dine penge retur.
                </div>
            );
        } else {
            return 'Dit BMI indikerer svær overvægt. Du har derfor mulighed for at følge Hejdoktors vægttabsforløb.';
        }
    }

    return (
        <>
            <div className="text-xl font-bold text-(--color-text-heading)">
                Dit BMI er {bmi.toFixed(1).replace('.', ',')}
            </div>
            <div className="my-4 py-1 text-lg leading-normal">{getText()}</div>
            <VerticalContainer
                spacing="sm"
                className="rounded-lg bg-(--color-bg-subtle) p-4 sm:px-8 sm:py-6"
            >
                <div
                    className="relative mb-1 flex justify-end text-center text-sm font-medium whitespace-nowrap"
                    style={{ width: getPointerPosition(), left: '23.5px' }}
                >
                    Dit BMI
                    <br />
                    &#9660;
                </div>
                <HorizontalContainer spacing="none" className="mb-2 max-w-full">
                    <VerticalContainer
                        spacing="none"
                        className="flex-1"
                        style={{ width: '28.33%' }}
                    >
                        <div className="h-8 rounded-2xl rounded-r-none border-t border-b border-l border-(--color-border-selected) bg-(--color-bg-attention-hover)" />
                        <div className="mt-2 text-center text-sm sm:text-lg">
                            &#60;18,5
                        </div>
                    </VerticalContainer>
                    <VerticalContainer
                        spacing="none"
                        className="flex-1"
                        style={{ width: '21.67%' }}
                    >
                        <div className="h-8 border border-(--color-border-selected) bg-(--color-bg-attention-hover)" />
                        <div className="mt-2 text-center text-sm sm:text-lg">
                            18,5-25
                        </div>
                    </VerticalContainer>
                    <VerticalContainer
                        spacing="none"
                        className="flex-1"
                        style={{ width: '16.67%' }}
                    >
                        <div className="text-font-body h-8 border-t border-r border-b border-(--color-border-selected) bg-(--color-bg-selected)" />
                        <div className="mt-2 text-center text-sm sm:text-lg">
                            25-30
                        </div>
                    </VerticalContainer>
                    <VerticalContainer
                        spacing="none"
                        className="flex-1"
                        style={{ width: '33.33%' }}
                    >
                        <div className="bg-primary-dark h-8 rounded-2xl rounded-l-none border-t border-r border-b border-(--color-border-selected)" />
                        <div className="mt-2 text-center text-sm sm:text-lg">
                            &#62;30
                        </div>
                    </VerticalContainer>
                </HorizontalContainer>
            </VerticalContainer>

            <div className="my-4 py-1 text-lg leading-normal" hidden={bmi < 27}>
                Inden du kan begynde på vores vægttabsprogram, vil vi bede dig
                besvare nogle korte spørgsmål om dine vaner, kost og
                velbefindende. Dine svar hjælper vores læger med at give dig den
                bedst mulige personlige rådgivning og behandling.
            </div>
            <div className="px-4 sm:px-8" hidden={bmi < 27}>
                <a
                    href={`/questionnaire/sundvaegt/?1000=${height}&2000=${weight}&a=1000,2000&q=1,20,271`}
                    className="btn btn-primary w-full"
                >
                    Start spørgeskema
                </a>
            </div>
            <div
                className="mt-6 flex cursor-pointer justify-center text-sm font-bold text-(--color-text-heading) no-underline"
                onClick={goBack}
            >
                <ArrowLeftIcon className="mr-2 h-2.5 w-3.5 self-center" />
                Tilbage til BMI-beregneren
            </div>
        </>
    );
}
